// BonBoZ: gắn/bỏ THẺ cho hội thoại — phần ruột dùng chung cho hai giao diện.
//
// Máy tính: lưới thẻ màu ngay dưới khung chat (`ComposerTagGrid.vue`) — đúng
// kiểu Pancake bản web, bấm một phát là xong, không rời mắt khỏi cuộc chat.
// Điện thoại: lưới đó ăn mất ba hàng của màn hình vốn đã chật, nên thay bằng
// nút thẻ trên thanh tiêu đề mở một bảng trượt (`BangGanThe.vue`) — đúng kiểu
// Pancake bản điện thoại.
//
// Hai giao diện, MỘT bộ luật: bộ thẻ nào được hiện, thẻ nào đang bật, bấm thì
// ghi ra sao. Tách ra đây để sửa luật một chỗ, không phải nhớ có hai bản.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { loadPageConfigs, locTheTheoTrang } from 'dashboard/helper/pageConfig';

import {
  mauChuTren,
  mauChuTrenNen,
  nenChipBat,
  tronMau,
} from 'dashboard/helper/labelContrast';

// Nền khung chat của từng theme; chip không được chọn nằm trong suốt trên đó
// nên màu chữ phải tính theo đúng nền này. Trùng với $bonboz-chat-*-nen trong
// _bonboz-theme.scss.
const NEN_CHAT_SANG = '#dbe8d3';
const NEN_CHAT_TOI = '#15181c';

// Chữ chip chỉ 11-13px nên đòi cao hơn ngưỡng WCAG 4.5 một bậc: ở cỡ đó, 4.5
// vẫn phải cố mắt mới liếc hết một lượt 20+ thẻ.
const NGUONG_CHIP = 5.5;

// Chip TẮT có nền tô nhạt bằng chính màu thẻ (xem `.bonboz-chip-tat`), nên chữ
// nằm trên màu NÀY chứ không phải nền khung chat trần. Phải khớp tỉ lệ bên
// SCSS — lệch thì chữ tụt xuống dưới ngưỡng mà không ai thấy.
const DUC_NEN_TAT = 0.14;
const nenTat = (mau, nenChat) => tronMau(mau, DUC_NEN_TAT, nenChat);

/**
 * Bốn biến CSS mà `.bonboz-chip-*` đọc. Dùng chung cho lưới (máy tính) và bảng
 * gắn thẻ (điện thoại) để hai nơi không bao giờ ra hai màu khác nhau.
 */
export function bienCssThe(mau) {
  return {
    '--tag-mau': mau,
    '--tag-nen-bat': nenChipBat(mau),
    '--tag-chu-nen': mauChuTrenNen(nenChipBat(mau)),
    '--tag-chu-sang': mauChuTren(mau, nenTat(mau, NEN_CHAT_SANG), NGUONG_CHIP),
    '--tag-chu-toi': mauChuTren(mau, nenTat(mau, NEN_CHAT_TOI), NGUONG_CHIP),
  };
}

// BonBoZ: tạm ẩn hai nhãn nhanh theo yêu cầu 21/08/2026. Chúng là label trong
// DB chứ không khai trong code, nên chỉ lọc lúc hiện — label và các hội thoại
// đã gắn vẫn nguyên. Bỏ tên khỏi mảng này là hiện lại.
const TAG_AN = ['Tư vấn ngay', 'Đã lấy số'];

// Lớp phủ lạc quan: {tên thẻ -> muốn bật hay tắt}, chỉ sống tới khi API trả về.
// Store của Chatwoot chỉ commit nhãn SAU khi API xong
// (store/modules/conversationLabels.js:42), mà trực page bấm liên tay vài thẻ
// một lúc — chờ cả vòng request mới đổi màu là thấy lag ngay.
//
// ĐỂ NGOÀI HÀM: nút thẻ trên thanh tiêu đề và bảng gắn thẻ là hai chỗ gọi khác
// nhau. Để bên trong thì mỗi chỗ có một lớp phủ riêng: bảng đổi tick ngay còn
// huy hiệu trên nút phải đợi hết vòng mạng, hai con số lệch nhau chừng một
// giây — đúng lúc người dùng đang nhìn cả hai.
const dangGui = ref(new Map());

export function useTheHoiThoai() {
  const { t } = useI18n();
  const store = useStore();
  const currentChat = useMapGetter('getSelectedChat');
  const { accountLabels, savedLabels, onUpdateLabels } =
    useConversationLabels();

  const inboxId = computed(() => currentChat.value?.inbox_id || null);

  onMounted(() => {
    // Thường sidebar đã nạp label rồi; đề phòng vào thẳng URL hội thoại.
    if (!accountLabels.value.length) store.dispatch('labels/get');
    // Bản đồ dùng chung cả app, hỏng thì tự degrade thành "hiện tất cả tag".
    loadPageConfigs();
  });

  const labelsHien = computed(() =>
    locTheTheoTrang(
      accountLabels.value.filter(l => !TAG_AN.includes(l.title)),
      inboxId.value
    )
  );

  // Đổi hội thoại thì bỏ hết lớp phủ đang treo: nó ghi theo TÊN thẻ, không
  // theo hội thoại, nên một request còn bay mà người dùng đã sang hội thoại
  // khác sẽ tô nhầm thẻ ở đó.
  watch(
    () => currentChat.value?.id,
    () => {
      if (dangGui.value.size) dangGui.value = new Map();
    }
  );

  const isActive = title =>
    dangGui.value.has(title)
      ? dangGui.value.get(title)
      : savedLabels.value.includes(title);

  const danhDau = (title, giaTri) => {
    const m = new Map(dangGui.value);
    if (giaTri === null) m.delete(title);
    else m.set(title, giaTri);
    dangGui.value = m; // gán lại để Vue nhận ra thay đổi
  };

  const toggle = async label => {
    const { title } = label;
    const bat = !isActive(title);
    // Dựng danh sách mới từ TOÀN BỘ nhãn đã lưu, không phải từ các thẻ đang
    // hiện: trang này có thể chỉ hiện một bộ tag riêng, lấy theo bộ đó là xoá
    // mất những nhãn thuộc bộ khác.
    const con = new Set(savedLabels.value);
    if (bat) con.add(title);
    else con.delete(title);

    danhDau(title, bat);
    try {
      await onUpdateLabels([...con]);
      // Thất bại thì store nuốt lỗi (chỉ bật cờ isError), nhãn giữ nguyên.
      if (store.getters['conversationLabels/getUIFlags'].isError) {
        useAlert(t('BONBOZ.TAG_GRID.SAVE_FAILED', { name: title }));
      }
    } finally {
      danhDau(title, null); // trả quyền quyết định lại cho store
    }
  };

  // Đếm theo các thẻ ĐANG HIỆN, không theo toàn bộ nhãn đã gắn: hội thoại có
  // thể mang nhãn thuộc bộ của trang khác, đếm cả vào thì con số trên huy hiệu
  // không khớp với những gì người dùng mở ra nhìn thấy.
  const soTheDaGan = computed(
    () => labelsHien.value.filter(l => isActive(l.title)).length
  );

  return { labelsHien, isActive, toggle, soTheDaGan };
}
